/**
 * Represents the structure of a tag object that react-select expects.
 * `value` is the unique identifier, and `label` is what the user sees.
 */
export interface TagOption {
    readonly value: string;
    readonly label: string;
}

/**
 * Fetches raw material tags from your backend.
 * @param inputValue The text typed by the user, to be used for filtering.
 * @returns A promise that resolves to an array of TagOption.
 */
export const fetchRawMaterialTags = async (inputValue: string): Promise<TagOption[]> => {
    // This function will call your existing backend endpoint.
    // We'll assume your endpoint can be filtered with a query parameter like `q`.
    const response = await fetch(`/api/tags?q=${encodeURIComponent(inputValue)}`);
    if (!response.ok) {
        // In a real app, you'd want more robust error handling here.
        throw new Error('Network response was not ok');
    }
    return response.json();
};

/**
 * Sends a new tag to the backend to be created.
 * @param newTagName The name of the new tag to create.
 * @returns A promise that resolves to the newly created TagOption.
 */
export const createRawMaterialTag = async (
    newTagName: string
): Promise<TagOption> => {
    // This function will call your existing backend endpoint to create a new tag.
    const response = await fetch('/api/tags', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            name: newTagName,
        }),
    });
    if (!response.ok) {
        // throw new Error('Failed to create new tag');
        const errorBody = await response.text();
        throw new Error(`Failed to create new tag: ${errorBody}`);

    }
    return response.json();
};

